// League Table — data + row shaping for the widget.
//
// Ported from the legacy renderer (renderers-tables.js) + data.js. The React
// component and the legacy innerHTML must produce identical rows; the visual
// A/B test guards that.
//
// MIGRATION FRICTION: LEAGUE_TABLE_DATA is dummy data duplicated from the legacy
// data.js so the component stays pure (data-in, no window access). When app-api
// supplies the table, delete this constant and pass the rows in as a prop.

import { clubFromName } from "../clubs/clubs.js";
import type { SortableColumn } from "../table/sort.js";

/** [name, P, W, D, L, GF, GA, GD, Pts] — the legacy row tuple. */
export type LeagueTeam = readonly [
  string,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
];

// The season we actually have real data for (WS-C). It must be one the season
// control offers (2025/26 | 2024/25) AND the key the real builder emits, so a
// module with no explicit season control still resolves to real rows.
export const LEAGUE_DEFAULT_SEASON = "2025/26";

export const LEAGUE_TABLE_DATA: Record<string, readonly LeagueTeam[]> = {
  "2026/27": [
    ["Arsenal", 38, 27, 6, 5, 88, 32, 56, 87],
    ["Liverpool", 38, 25, 7, 6, 82, 38, 44, 82],
    ["Manchester City", 38, 24, 7, 7, 78, 38, 40, 79],
    ["Chelsea", 38, 21, 8, 9, 68, 42, 26, 71],
    ["Aston Villa", 38, 19, 9, 10, 63, 42, 21, 66],
    ["Newcastle", 38, 18, 8, 12, 59, 43, 16, 62],
    ["Tottenham", 38, 17, 8, 13, 56, 45, 11, 59],
    ["Brighton", 38, 15, 11, 12, 54, 45, 9, 56],
    ["Manchester United", 38, 15, 8, 15, 50, 48, 2, 53],
    ["Brentford", 38, 14, 9, 15, 47, 48, -1, 51],
    ["Fulham", 38, 13, 9, 16, 45, 50, -5, 48],
    ["Bournemouth", 38, 13, 7, 18, 43, 52, -9, 46],
    ["Everton", 38, 11, 10, 17, 41, 52, -11, 43],
    ["Crystal Palace", 38, 11, 9, 18, 39, 53, -14, 42],
    ["West Ham", 38, 10, 10, 18, 37, 54, -17, 40],
    ["Wolverhampton", 38, 10, 8, 20, 36, 58, -22, 38],
    ["Nottingham Forest", 38, 9, 8, 21, 33, 60, -27, 35],
    ["Sunderland", 38, 8, 7, 23, 29, 63, -34, 31],
    ["Leeds United", 38, 7, 8, 23, 27, 65, -38, 29],
    ["Burnley", 38, 5, 6, 27, 22, 69, -47, 21],
  ],
  "2025/26": [
    ["Arsenal", 38, 26, 8, 4, 86, 33, 53, 86],
    ["Liverpool", 38, 24, 7, 7, 79, 35, 44, 79],
    ["Manchester City", 38, 22, 9, 7, 74, 38, 36, 75],
    ["Chelsea", 38, 20, 9, 9, 67, 40, 27, 69],
    ["Aston Villa", 38, 19, 8, 11, 62, 42, 20, 65],
    ["Newcastle", 38, 18, 8, 12, 59, 45, 14, 62],
    ["Tottenham", 38, 16, 9, 13, 55, 46, 9, 57],
    ["Brighton", 38, 15, 10, 13, 53, 46, 7, 55],
    ["Manchester United", 38, 14, 9, 15, 50, 47, 3, 51],
    ["Brentford", 38, 13, 10, 15, 47, 48, -1, 49],
    ["Fulham", 38, 13, 8, 17, 45, 51, -6, 47],
    ["Bournemouth", 38, 12, 8, 18, 42, 52, -10, 44],
    ["Everton", 38, 11, 10, 17, 40, 53, -13, 43],
    ["Crystal Palace", 38, 11, 9, 18, 39, 54, -15, 42],
    ["West Ham", 38, 10, 9, 19, 37, 56, -19, 39],
    ["Nottingham Forest", 38, 9, 8, 21, 34, 58, -24, 35],
    ["Wolverhampton", 38, 8, 9, 21, 32, 60, -28, 33],
    ["Sunderland", 38, 8, 7, 23, 28, 64, -36, 31],
    ["Leeds United", 38, 6, 8, 24, 24, 67, -43, 26],
    ["Burnley", 38, 4, 6, 28, 20, 72, -52, 18],
  ],
  "2024/25": [
    ["Liverpool", 38, 24, 10, 4, 84, 36, 48, 82],
    ["Arsenal", 38, 22, 8, 8, 77, 39, 38, 74],
    ["Nottingham Forest", 38, 19, 9, 10, 63, 43, 20, 66],
    ["Chelsea", 38, 18, 9, 11, 62, 44, 18, 63],
    ["Manchester City", 38, 18, 8, 12, 61, 45, 16, 62],
    ["Newcastle", 38, 17, 9, 12, 59, 47, 12, 60],
    ["Aston Villa", 38, 16, 9, 13, 56, 49, 7, 57],
    ["Brighton", 38, 14, 10, 14, 53, 50, 3, 52],
    ["Bournemouth", 38, 13, 8, 17, 49, 48, 1, 47],
    ["Fulham", 38, 13, 7, 18, 46, 50, -4, 46],
    ["West Ham", 38, 12, 9, 17, 45, 51, -6, 45],
    ["Brentford", 38, 12, 8, 18, 43, 52, -9, 44],
    ["Everton", 38, 11, 9, 18, 41, 54, -13, 42],
    ["Crystal Palace", 38, 10, 10, 18, 38, 54, -16, 40],
    ["Manchester United", 38, 9, 9, 20, 35, 56, -21, 36],
    ["Tottenham", 38, 9, 8, 21, 34, 57, -23, 35],
    ["Wolverhampton", 38, 8, 9, 21, 33, 59, -26, 33],
    ["Ipswich Town", 38, 7, 8, 23, 28, 67, -39, 29],
    ["Leicester City", 38, 6, 6, 26, 25, 69, -44, 24],
    ["Southampton", 38, 3, 5, 30, 18, 75, -57, 14],
  ],
};

/**
 * League table columns, in display order (the rank "#" column is added by the
 * component, not here). `cls` is the exact legacy cell class; sortable columns
 * carry their sort type. Reused for headers, sorting and cell rendering.
 */
export interface LeagueColumn extends SortableColumn {
  label: string;
  /** Legacy cell class(es), e.g. "col-num col-opt". */
  cls: string;
}

export const LEAGUE_COLUMNS: readonly LeagueColumn[] = [
  { key: "team", label: "Team", type: "text", cls: "col-team" },
  { key: "p", label: "P", type: "num", cls: "col-num" },
  { key: "w", label: "W", type: "num", cls: "col-num col-opt" },
  { key: "d", label: "D", type: "num", cls: "col-num col-opt" },
  { key: "l", label: "L", type: "num", lowFirst: true, cls: "col-num col-opt" },
  { key: "gf", label: "GF", type: "num", cls: "col-num col-wide" },
  { key: "ga", label: "GA", type: "num", lowFirst: true, cls: "col-num col-wide" },
  { key: "gd", label: "GD", type: "num", cls: "col-num" },
  { key: "pts", label: "Pts", type: "num", cls: "col-num pts" },
];

export interface LeagueRow {
  /** Canonical team name — React key + highlight source. */
  name: string;
  /** Displayed team cell: full name on desktop, short code on mobile. */
  label: string;
  p: number;
  w: number;
  d: number;
  l: number;
  gf: number;
  ga: number;
  /** Raw goal difference (for sorting). */
  gd: number;
  /** Formatted goal difference: "+56" / "0" / "-4" (for display). */
  gdText: string;
  pts: number;
  /** True when the row is one of the fixture's two teams. */
  highlighted: boolean;
}

/** Team cell label: full name on desktop, 3-letter short code on mobile. */
export function teamLabel(name: string, isMobile: boolean): string {
  if (!isMobile) return name;
  const club = clubFromName(name);
  return club ? club.short : name.substring(0, 3).toUpperCase();
}

/** Goal difference formatted with an explicit sign, "0" for zero. */
export function formatGD(n: number): string {
  return n > 0 ? "+" + n : n === 0 ? "0" : String(n);
}

/**
 * Shape raw team tuples into display rows for a fixture. `isMobile` picks the
 * team label form; a row is highlighted when its name contains either fixture
 * team (the legacy substring match).
 */
export function shapeLeagueRows(
  teams: readonly LeagueTeam[],
  fixture: string,
  isMobile: boolean
): LeagueRow[] {
  const parts = (fixture || "Arsenal v Everton").split(" v ");
  const t1 = (parts[0] ?? "").trim().toLowerCase();
  const t2 = (parts[1] ?? "").trim().toLowerCase();
  return teams.map((t) => {
    const name = t[0];
    const lower = name.toLowerCase();
    const highlighted =
      (t1 !== "" && lower.indexOf(t1) !== -1) ||
      (t2 !== "" && lower.indexOf(t2) !== -1);
    return {
      name,
      label: teamLabel(name, isMobile),
      p: t[1],
      w: t[2],
      d: t[3],
      l: t[4],
      gf: t[5],
      ga: t[6],
      gd: t[7],
      gdText: formatGD(t[7]),
      pts: t[8],
      highlighted,
    };
  });
}
